import {memo, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    Box,
    Button,
    Checkbox,
    ClickAwayListener,
    Collapse,
    Divider,
    FormControlLabel,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {RasterLayerType} from "../../types/domain/rasterLayerType";
import {AggregatedLayerType} from "../../types/domain/aggregatedLayerType";
import {
    AGGREGATED_LAYERS,
    createLayersMapFromLayers,
    isAggregatedLayerType,
    RasterLayerProps,
} from "../../utils/layersUtils";
import {ChosenLayerType, MapSelectedLayers} from "../../types/mapSelectedLayers";
import {LayersIcon} from "../icons/LayersIcon";
import {HelpPopover} from "../base/HelpPopover.tsx";
import analytics from "../../services/analyticsService";
import {swallowException} from "../../utils/appUtils";

export interface Props {
    mapSelectedLayers: MapSelectedLayers;
    setMapSelectedLayers: (value: MapSelectedLayers) => void;
    enableMaderaFarmUnitsLayer: boolean;
    availableRasterLayers: RasterLayerProps[];
    onStyleChange: () => void;
}
interface CustomInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    "data-layer-type"?: string;
}

interface LayerOption {
    type: RasterLayerType | AggregatedLayerType;
    layerType: "raster" | "aggregated";
    label: string;
}

// eslint-disable-next-line react/display-name
export const MapLayerSelector: React.FC<Props> = memo(
    ({mapSelectedLayers, setMapSelectedLayers, enableMaderaFarmUnitsLayer, availableRasterLayers, onStyleChange}) => {
        const {t, i18n} = useTranslation();
        const [opened, setOpened] = useState(false);
        const AVAILABLE_RASTER_LAYERS_MAP = createLayersMapFromLayers(availableRasterLayers);

        let selectedLayerName: string;
        let chosenLayer: ChosenLayerType;
        if (isAggregatedLayerType(mapSelectedLayers.layerType)) {
            chosenLayer = mapSelectedLayers.chosenLayer as AggregatedLayerType;
            selectedLayerName = t(`map.map_layer_selection.layers.${chosenLayer}`);
        } else {
            chosenLayer = mapSelectedLayers.chosenLayer as RasterLayerType;
            selectedLayerName = i18n.exists(`map.map_layer_selection.layers.${chosenLayer}`)
                ? t(`map.map_layer_selection.layers.${chosenLayer}`)
                : AVAILABLE_RASTER_LAYERS_MAP[chosenLayer].name;
        }

        const getLayerOptions = (): LayerOption[] => {
            const firstLayer = availableRasterLayers
                .filter((item) => item.type === RasterLayerType.MOISTURE_STATUS)
                .map(({type}) => ({
                    type,
                    layerType: "raster" as const,
                    label: t(`map.map_layer_selection.layers.${type}`),
                }));

            const aggregatedLayers = AGGREGATED_LAYERS(t).map(({type}) => ({
                type,
                layerType: "aggregated" as const,
                label: t(`map.map_layer_selection.layers.${type}`),
            }));

            const otherRasterLayers = availableRasterLayers
                .filter((item) => item.type !== RasterLayerType.MOISTURE_STATUS)
                .map(({type}) => ({
                    type,
                    layerType: "raster" as const,
                    label: t(`map.map_layer_selection.layers.${type}`),
                }));

            return [...firstLayer, ...aggregatedLayers, ...otherRasterLayers];
        };

        return (
            <ClickAwayListener onClickAway={() => setOpened(false)}>
                <Box>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => setOpened(!opened)}
                        sx={{height: "2rem", padding: "0.5rem"}}
                    >
                        <Stack direction="row" gap="0.5rem" alignItems="center">
                            <LayersIcon
                                sx={{stroke: "#404DB8", width: "1.1rem", height: "1.1rem", paddingX: "0.2rem"}}
                            />
                            <Typography
                                textTransform="none"
                                color="#3A3F4F"
                                fontSize={16}
                                textOverflow="ellipsis"
                                noWrap
                            >
                                {selectedLayerName}
                            </Typography>
                            {opened ? (
                                <ExpandLessIcon
                                    sx={{
                                        width: "1.4rem",
                                        height: "1.4rem",
                                        color: "#8F94A8",
                                    }}
                                />
                            ) : (
                                <ExpandMoreIcon
                                    sx={{
                                        width: "1.4rem",
                                        height: "1.4rem",
                                        color: "#8F94A8",
                                    }}
                                />
                            )}
                        </Stack>
                    </Button>
                    <Collapse in={opened} sx={{position: "absolute", minWidth: "15rem"}}>
                        <Stack
                            component={Paper}
                            elevation={0}
                            square
                            sx={{borderRadius: "0.25rem", padding: "1.125rem"}}
                        >
                            <RadioGroup
                                value={mapSelectedLayers.chosenLayer}
                                onChange={(event) => {
                                    const layerName = event.target.value as RasterLayerType | AggregatedLayerType;
                                    const layerType = event.currentTarget?.getAttribute("data-layer-type");
                                    if (!layerType) {
                                        return;
                                    }
                                    analytics
                                        .track("map-layer-select", {
                                            layer: layerName,
                                            layertype: layerType,
                                        })
                                        .catch(swallowException);
                                    setMapSelectedLayers({
                                        ...mapSelectedLayers,
                                        chosenLayer: layerName,
                                        layerType,
                                    });
                                }}
                            >
                                {getLayerOptions().map(({type, layerType, label}) => (
                                    <Stack direction="row" gap="3rem" key={type} sx={{alignItems: "center"}}>
                                        <FormControlLabel
                                            key={type}
                                            value={type}
                                            control={
                                                <Radio
                                                    sx={{paddingY: "0.3rem"}}
                                                    inputProps={{"data-layer-type": layerType} as CustomInputProps}
                                                />
                                            }
                                            label={label}
                                            sx={{"& .MuiFormControlLabel-label": {whiteSpace: "nowrap"}, flexGrow: 1}}
                                        />
                                        {type === RasterLayerType.NONE && (
                                            <HelpPopover
                                                i18nKey={`help.popover.map.${type}`}
                                                direction="right"
                                                placement="center"
                                            />
                                        )}
                                    </Stack>
                                ))}
                            </RadioGroup>
                            <Divider sx={{marginY: "0.5rem"}} />
                            <FormControlLabel
                                label={t("map.map_layer_selector.satellite")}
                                control={
                                    <Checkbox
                                        style={{paddingTop: "0.25rem", paddingBottom: "0.25rem"}}
                                        checked={mapSelectedLayers.satellite}
                                        onChange={(event) => {
                                            setMapSelectedLayers({
                                                ...mapSelectedLayers,
                                                satellite: event.target.checked,
                                            });
                                            onStyleChange();
                                        }}
                                    />
                                }
                            />
                            <FormControlLabel
                                label={t("map.map_layer_selector.streets")}
                                control={
                                    <Checkbox
                                        style={{paddingTop: "0.25rem", paddingBottom: "0.25rem"}}
                                        checked={mapSelectedLayers.streets}
                                        onChange={(event) => {
                                            setMapSelectedLayers({...mapSelectedLayers, streets: event.target.checked});
                                            onStyleChange();
                                        }}
                                    />
                                }
                            />
                            {enableMaderaFarmUnitsLayer && (
                                <Stack direction="row">
                                    <FormControlLabel
                                        label={t("map.map_layer_selector.madera")}
                                        sx={{flexGrow: 1}}
                                        control={
                                            <Checkbox
                                                style={{paddingTop: "0.25rem", paddingBottom: "0.25rem"}}
                                                checked={mapSelectedLayers.madera}
                                                onChange={(event) => {
                                                    setMapSelectedLayers({
                                                        ...mapSelectedLayers,
                                                        madera: event.target.checked,
                                                    });
                                                }}
                                            />
                                        }
                                    />
                                    <HelpPopover
                                        i18nKey="help.popover.map.madera_farm_units"
                                        direction="right"
                                        placement="center"
                                    />
                                </Stack>
                            )}
                        </Stack>
                    </Collapse>
                </Box>
            </ClickAwayListener>
        );
    },
);
